// src/hooks/useClubs.ts

import { useState, useCallback, useEffect, useRef } from "react";
import { ClubModel } from "../models/ClubModel";
import { clubService } from "../services/clubService";

const CLUBS_PAGE_SIZE = 20;

export type ClubsSortOption = "lastEntryAt" | "recentEntryCount";

/**
 * @param autoLoad          Load the first page on mount.
 * @param showRookieEntries Viewer preference. Forwarded to the API so the badge
 *        counts (and the "most active" ordering) exclude rookie-authored entries
 *        when the viewer has them hidden — otherwise the pane advertised entries
 *        the club page never rendered. Mirrors useCustomTopics.
 */
export const useClubs = (autoLoad: boolean = true, showRookieEntries: boolean = true) => {
  const [clubs, setClubs]             = useState<ClubModel[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalItems, setTotalItems]   = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [sortBy, setSortBy]           = useState<ClubsSortOption>("recentEntryCount");
  // Left-pane country filter — null means all countries
  const [countryFilter, setCountryFilter] = useState<string | null>(null);
  // Kept in a ref so loadClubs stays a stable useCallback while still reading the
  // current preference value (same stale-closure guard used by useCustomTopics).
  const showRookieRef = useRef(showRookieEntries);
  showRookieRef.current = showRookieEntries;

  /**
   * Core loader. Pass `append=true` to add the next page to the list.
   * Pass `silent=true` to skip the loading flag so the existing list
   * stays visible during refresh (matches pane Yenile behaviour).
   */
  const loadClubs = useCallback(async (
    sort: ClubsSortOption = "recentEntryCount",
    page = 0,
    append = false,
    silent = false,
    country: string | null = null
  ) => {
    if (append) {
      setIsLoadingMore(true);
    } else if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const sortDir = "desc"; // both sorts are descending (most recent / most active first)
      const result = await clubService.getAllClubs(page, CLUBS_PAGE_SIZE, sort, sortDir, country, showRookieRef.current);
      if (append) {
        setClubs(prev => [...prev, ...result.content]);
      } else {
        setClubs(result.content);
      }
      setCurrentPage(page);
      setTotalPages(result.totalPages);
      setTotalItems(result.totalItems);
    } catch (err) {
      console.error('[useClubs] loadClubs error:', err);
      setError("Kulüpler yüklenemedi.");
    } finally {
      if (!silent) setLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  // Load on mount — skipped when autoLoad is false so the parent can lazy-load
  // this pane only when the user first switches to it.
  useEffect(() => {
    if (autoLoad) loadClubs("recentEntryCount", 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad]);

  // Reload when the viewer flips the "show rookie entries" preference, so the
  // badge counts refresh without requiring a manual Yenile.
  const isFirstPrefRun = useRef(true);
  useEffect(() => {
    if (isFirstPrefRun.current) {
      isFirstPrefRun.current = false;
      return;
    }
    loadClubs(sortBy, 0, false, true, countryFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRookieEntries]);

  const loadMoreClubs = useCallback(async () => {
    const nextPage = currentPage + 1;
    if (isLoadingMore || nextPage >= totalPages) return;
    await loadClubs(sortBy, nextPage, true, false, countryFilter);
  }, [currentPage, totalPages, isLoadingMore, loadClubs, sortBy, countryFilter]);

  /**
   * Silent refresh — fetches fresh data without triggering the loading state,
   * so the list stays visible during the request (matches pane behaviour).
   */
  const refreshClubs = useCallback(async () => {
    await loadClubs(sortBy, 0, false, true, countryFilter);
  }, [loadClubs, sortBy, countryFilter]);

  const handleSortChange = useCallback((newSort: ClubsSortOption) => {
    setSortBy(newSort);
    loadClubs(newSort, 0, false, false, countryFilter);
  }, [loadClubs, countryFilter]);

  /**
   * Left-pane country filter change — resets to page 0 and reloads.
   * Pass null to clear the filter (all countries).
   */
  const handleCountryFilterChange = useCallback((country: string | null) => {
    setCountryFilter(country);
    loadClubs(sortBy, 0, false, false, country);
  }, [loadClubs, sortBy]);

  return {
    clubs,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    isLoadingMore,
    sortBy,
    countryFilter,
    loadClubs,
    loadMoreClubs,
    refreshClubs,
    handleSortChange,
    handleCountryFilterChange,
  };
};